// Shotgun starts. Pure functions, no imports outside domain types.
//
// At an outing every group tees off at once on a different hole, so a group starting on 5
// plays 5,6,…,18,1,2,3,4 and finishes where the group ahead of them began. Only the ORDER
// changes: the same holes are played, worth the same, against the same stroke indexes.
//
// That distinction is what keeps this module small and the engines untouched. Scoring is
// indexed by hole (`round.holes[i]` lines up with `tee.holes[i]`) and stays that way — a
// team's total is the same however they got round. What a shotgun changes is navigation
// ("next hole" wraps past the last one) and progress ("thru 7" means seven holes PLAYED,
// which on a shotgun is not the same as holes 1 through 7).

import type { Round } from '../types';

/** Normalise a start hole into range. Absent, negative, or past the end all mean hole one. */
export function normalizeStartHole(startHole: number | undefined, holeCount: number): number {
  if (holeCount <= 0) return 0;
  if (startHole == null || !Number.isFinite(startHole)) return 0;
  const i = Math.trunc(startHole);
  if (i < 0 || i >= holeCount) return 0;
  return i;
}

/**
 * The hole indexes in the order this group plays them.
 *
 * `playOrder(18, 4)` → [4,5,…,17,0,1,2,3]. With no shotgun it is simply [0…n-1].
 */
export function playOrder(holeCount: number, startHole?: number): number[] {
  if (holeCount <= 0) return [];
  const start = normalizeStartHole(startHole, holeCount);
  return Array.from({ length: holeCount }, (_, n) => (start + n) % holeCount);
}

/**
 * Where a hole falls in this group's round, 1-based.
 *
 * On a shotgun from hole 5, hole index 4 is their 1st played and hole index 3 is their 18th.
 * This is the number to show as "hole 3 of 18" while someone is standing on it.
 */
export function playPosition(holeIdx: number, holeCount: number, startHole?: number): number {
  if (holeCount <= 0) return 0;
  const start = normalizeStartHole(startHole, holeCount);
  return (((holeIdx - start) % holeCount) + holeCount) % holeCount + 1;
}

/**
 * Step forward or back through the round, wrapping at both ends.
 *
 * Wrapping is plain modulo and needs no start hole: a shotgun round is a loop, and moving
 * off the last hole lands on the first whatever hole you began on.
 */
export function stepHole(holeIdx: number, delta: number, holeCount: number): number {
  if (holeCount <= 0) return 0;
  return (((holeIdx + delta) % holeCount) + holeCount) % holeCount;
}

/** True when every one of `playerIds` has a gross score on the hole. */
function scored(round: Round, playerIds: string[], holeIdx: number): boolean {
  const hole = round.holes[holeIdx];
  if (!hole) return false;
  return playerIds.length > 0 && playerIds.every((id) => hole.scores[id]?.gross != null);
}

/**
 * How many holes this group has actually finished, wherever they sit on the card.
 *
 * The count a leaderboard means by "thru 7". Deliberately NOT a leading run: a group that
 * started on 5 has completed nothing at the front of the array, and `completeThrough` —
 * which every legacy engine uses — would report zero for them all afternoon.
 */
export function holesPlayed(round: Round, playerIds: string[]): number {
  let n = 0;
  for (let i = 0; i < round.holes.length; i++) if (scored(round, playerIds, i)) n++;
  return n;
}

/**
 * The next hole this group still owes a score on, searching forward in play order from
 * `from` and wrapping. Returns null once the card is full — which is how a caller knows
 * the round is done rather than looping for ever.
 */
export function nextUnplayedHole(
  round: Round,
  playerIds: string[],
  from: number
): number | null {
  const holeCount = round.holes.length;
  if (holeCount <= 0) return null;
  for (let n = 0; n < holeCount; n++) {
    const idx = stepHole(from, n, holeCount);
    if (!scored(round, playerIds, idx)) return idx;
  }
  return null;
}
